import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router';

/** Date.now(), re-rendered every `ms`. */
export function useNow(ms = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(iv); }, [ms]);
  return now;
}

/** A value kept in localStorage (per-browser UI preference only — never data). */
export function usePref(key, initial) {
  const [v, setV] = useState(() => {
    try { const raw = localStorage.getItem(`ops.web.${key}`); return raw === null ? initial : JSON.parse(raw); } catch { return initial; }
  });
  const set = useCallback((next) => {
    setV((prev) => {
      const val = typeof next === 'function' ? next(prev) : next;
      try { localStorage.setItem(`ops.web.${key}`, JSON.stringify(val)); } catch { /* private mode */ }
      return val;
    });
  }, [key]);
  return [v, set];
}

/** The right-hand detail panel lives in the URL (?task= / ?session= / ?event=) so any page can open it. */
export function usePanel() {
  const [params, setParams] = useSearchParams();
  const open = useCallback((kind, id) => {
    setParams((p) => { const n = new URLSearchParams(p); ['task', 'session', 'event'].forEach((k) => n.delete(k)); if (id) n.set(kind, id); return n; });
  }, [setParams]);
  const close = useCallback(() => open(null, null), [open]);
  const kind = ['task', 'session', 'event'].find((k) => params.get(k)) || null;
  return { kind, id: kind ? params.get(kind) : null, open, close };
}

/** Global keyboard shortcut; skipped while typing in a field unless `inFields`. */
export function useKey(combo, fn, { inFields = false, enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;
    const parts = combo.toLowerCase().split('+');
    const key = parts.pop();
    const want = { mod: parts.includes('mod'), shift: parts.includes('shift'), alt: parts.includes('alt') };
    const on = (e) => {
      const t = e.target;
      const typing = t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName));
      if (typing && !inFields) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod !== want.mod || e.shiftKey !== want.shift || e.altKey !== want.alt) return;
      // '/' by its key position too, so Ctrl Shift / works (Shift turns the key into '?').
      const pressed = e.code === 'Slash' ? '/' : e.key.toLowerCase();
      if (pressed !== key) return;
      e.preventDefault();
      fn(e);
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [combo, fn, inFields, enabled]);
}

/** The big task view (components/TaskModal) lives in the URL too (?details=<taskId>) — open it from anywhere. */
export function useTaskModal() {
  const [params, setParams] = useSearchParams();
  const open = useCallback((id) => setParams((p) => { const n = new URLSearchParams(p); if (id) { n.set('details', id); n.delete('task'); } else n.delete('details'); return n; }), [setParams]);
  return { id: params.get('details'), open, close: useCallback(() => open(null), [open]) };
}
